import { useEffect, useRef, useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";

import { APPREG_SCOPE } from "@/lib/auth";
import { onAuthenticationRequired } from "@/lib/auth-events";
import {
  getPortalAccessToken,
  isPortalTokenEnabled,
} from "@/lib/embed/portalTokenProvider";
import { useStore } from "@/state/useState";
import { Button } from "@/components/ui/button";

/**
 * How the portal token handoff is going (MA-227).
 *
 * - `off` — the flag is off, or the build is not an embed, or the configuration
 *   is incomplete. Nothing below this line runs and the gate behaves exactly as
 *   it did before MA-227.
 * - `trying` — one exchange with the host portal is in flight.
 * - `ok` — we hold a portal token. The chat renders, and `acquireAccessToken`
 *   will keep using the same path for every API call.
 * - `unavailable` — the portal did not give us one. Today's flow takes over.
 */
type PortalState = "off" | "trying" | "ok" | "unavailable";

/**
 * Auth gate for embedded (portal iframe) mode.
 *
 * Redirects can't run inside the iframe — the Entra login page refuses to be
 * framed — so we try silent SSO first (shared-tenant session) and fall back to
 * a popup on an explicit user gesture. Children (the chat) render only once the
 * user is authenticated; config is fetched on that same transition since it
 * needs a token.
 *
 * On top of that, when the MA-227 flag is on, the gate first asks the host portal
 * to hand us a token (`TokenHandler`, see `lib/embed/portalToken.ts`). That path
 * removes the one-time "Aanmelden met Microsoft" click entirely — the user is
 * already signed in to the portal, and the portal can mint for our API because
 * its client is pre-authorized on our scope
 * (`Terraform/Infrastructure/appregistration.tf`).
 *
 * Note what "authenticated" has to mean here as a result. `useIsAuthenticated()`
 * reflects the MSAL cache, and a portal token never enters it — `MsalProvider`
 * stays mounted and MSAL stays signed out. So the hook is false with a perfectly
 * good token in hand, and must stay false: making it lie would mean writing a
 * fake account into the MSAL cache. The gate therefore computes its own
 * predicate, `isAuthenticated || portalState === "ok"`, and that is what gates
 * both the children and the config fetch. Nothing else in the embed tree needs
 * an MSAL account — `useMsal`/`getActiveAccount` in `src/components` reaches only
 * this file and `Sidebar.tsx`, which embed mode does not render — and the backend
 * takes its identity from the token's `oid` either way.
 *
 * The two paths run SEQUENTIALLY, never concurrently, and that is deliberate.
 * Two effects racing would both `setState` and the order of two network calls
 * would decide which screen the user sees. The cost is bounded: the portal
 * exchange has a 2500 ms timeout, so the worst case is 2.5 s of the existing
 * "Aanmelden…" spinner before today's flow starts, with no new UI anywhere.
 *
 * `ssoSilent` is never attempted while the portal path is live. It is known not
 * to complete in-frame — both Safari and Chrome block the third-party cookie the
 * hidden auth iframe needs (MA-121 spike, `frontend/docs/portal-embed.md` §5) —
 * so attempting it would only add a failing round trip to the one path that was
 * supposed to be fast.
 */
export default function EmbedAuthGate({
  children,
}: {
  children: React.ReactNode;
}) {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [ssoFailed, setSsoFailed] = useState(false);
  // Read once, at mount, rather than on every render: the flag cannot change
  // during a page load, and `off` has to be a stable fact for the effects below
  // to key off.
  const [portalState, setPortalState] = useState<PortalState>(() =>
    isPortalTokenEnabled() ? "trying" : "off",
  );
  const attemptedPortal = useRef(false);
  const attemptedSso = useRef(false);
  const fetchedConfig = useRef(false);
  const fetchConfig = useStore((state) => state.fetchConfig);

  // The current `portalState`, readable from a subscription that was registered
  // once. The `onAuthenticationRequired` effect below has empty deps on purpose
  // (re-registering it on every state change would drop and re-add the listener
  // mid-flight), so it cannot close over `portalState` -- and it must not read it
  // from inside a `setPortalState` updater either, because updaters have to be
  // pure. This ref is the third option, and the only one that is both.
  const portalStateRef = useRef(portalState);
  useEffect(() => {
    portalStateRef.current = portalState;
  }, [portalState]);

  const authed = isAuthenticated || portalState === "ok";

  // Ask the portal for a token, once, before anything else is tried.
  //
  // No timeout of its own: `getPortalAccessToken` cannot hang (the exchange
  // underneath rejects after `PORTAL_TOKEN_TIMEOUT_MS`) and it cannot throw —
  // every failure it can have, including a malformed reply and a portal that
  // never answers, comes back as `undefined`.
  useEffect(() => {
    if (portalState !== "trying") return;
    if (attemptedPortal.current) return;

    attemptedPortal.current = true;

    // No cleanup, and deliberately no `cancelled` flag — the same shape as the
    // `ssoSilent` effect below, which guards on `attemptedSso` and nothing else.
    //
    // A `cancelled` closure flag was tried here and is a trap, because it does not
    // survive what `attemptedPortal` survives. Under StrictMode's development
    // double-invoke: the first run posts the exchange and captures `cancelled`,
    // React's synthetic cleanup sets that same variable true, and the second run
    // returns early on the now-true ref — so no fresh, unflipped flag is ever
    // registered. The token then resolves into a guard that is permanently true,
    // the state update is dropped, and the gate sits on "Aanmelden…" forever with
    // children that never render.
    //
    // It bought only the suppression of an `act()` warning when a resolve lands
    // after unmount. React 18 does not warn about setting state on an unmounted
    // component, so that is test noise, and not worth a dev-mode deadlock.
    getPortalAccessToken().then((token) => {
      setPortalState(token ? "ok" : "unavailable");
    });
  }, [portalState]);

  // Come back to the sign-in prompt when the API reports that credentials no
  // longer work.
  //
  // This is what turns "the portal gave us a token our backend rejects" into
  // "the user sees the normal Aanmelden button" instead of a chat in which every
  // request fails silently — the same failure mode, and the same remedy, as
  // `TeamsAuthGate` (MA-202). The gate cannot see it on its own: it only ever
  // acquires a token, it never calls the API.
  //
  // GATED ON THE FLAG, not just on the state. With MA-227 off this subscription
  // must not exist at all: the standalone and embed builds rely on MSAL's own
  // re-authentication today, and adding a listener that re-renders the gate would
  // change flag-off behaviour, which it must not.
  //
  // Guarded on `ok` so a late signal from an in-flight request cannot overwrite a
  // state the gate's own flow currently owns.
  //
  // The guard and the three side effects sit OUTSIDE the `setPortalState`
  // updater, reading the current state from `portalStateRef`. They used to be
  // inside it, which is a React rule violation with a visible consequence rather
  // than a style point: a state updater must be pure, and StrictMode deliberately
  // invokes it twice to catch exactly this. Inside the updater, the re-auth path
  // logged its warning twice, called `setSsoFailed` twice, and wrote
  // `attemptedSso.current` twice per signal. Plain statements in a plain `if`
  // instead -- the updater is now a constant.
  useEffect(() => {
    if (!isPortalTokenEnabled()) return;

    return onAuthenticationRequired(() => {
      if (portalStateRef.current !== "ok") return;

      console.warn("[portal-token] token no longer accepted; asking to sign in");
      // Straight to the button, skipping `ssoSilent`: it cannot complete
      // in-frame (§5 above), so running it here would only put the spinner back
      // for a round trip that is already known to fail. The ref keeps the
      // effect below from starting it behind the button.
      attemptedSso.current = true;
      setSsoFailed(true);
      setPortalState("unavailable");
    });
  }, []);

  // Silent SSO once, on mount, when no session is present.
  useEffect(() => {
    // Never while the portal path is still live: either it is about to hand us a
    // token, or it already has.
    if (portalState === "trying" || portalState === "ok") return;
    if (attemptedSso.current) return;
    if (inProgress !== InteractionStatus.None) return;
    if (isAuthenticated || instance.getAllAccounts().length > 0) return;

    attemptedSso.current = true;
    instance
      .ssoSilent({ scopes: [APPREG_SCOPE] })
      .then((result) => {
        if (result.account) instance.setActiveAccount(result.account);
      })
      .catch(() => setSsoFailed(true));
  }, [instance, inProgress, isAuthenticated, portalState]);

  // Config needs a token, so fetch it only once authenticated — by either route.
  // A portal token is a token.
  //
  // The ref is RESET when `authed` goes false, and that reset is the whole point
  // of this effect having a body instead of a one-line condition. `authed` is not
  // monotonic once the portal path exists, and this sequence is reachable:
  //
  //   portal token accepted -> authed -> config fetched -> our backend refuses
  //   the token -> the MSAL retry fails too -> the gate drops to the
  //   "Aanmelden met Microsoft" button -> the user signs in -> authed again.
  //
  // With the ref left true across that dip, the second `authed` never fetches and
  // the chat renders with `config: null` for the rest of the page load. That is
  // not a cosmetic gap, and it gets worse the more the config carries: the
  // opening greeting (PR 292, "Open a conversation with Sam's greeting") reads
  // `config?.chat.greeting` in `ChatInterface.tsx`, so once the two land together
  // a null config means Sam silently stops introducing himself, with nothing
  // anywhere reporting a failure.
  //
  // Still a ref and not a plain dependency, because the job it does is unchanged:
  // one fetch per authenticated session, not one per render or per token refresh.
  useEffect(() => {
    if (!authed) {
      fetchedConfig.current = false;
      return;
    }
    if (fetchedConfig.current) return;

    fetchedConfig.current = true;
    fetchConfig();
  }, [authed, fetchConfig]);

  if (authed) return <>{children}</>;

  const busy =
    portalState === "trying" ||
    inProgress !== InteractionStatus.None ||
    !ssoFailed;
  if (busy) {
    return (
      <div className="grid h-full w-full place-items-center text-sm text-muted-foreground">
        Aanmelden…
      </div>
    );
  }

  return (
    <div className="grid h-full w-full place-items-center p-4">
      <Button
        variant="outline"
        className="border border-brand"
        onClick={() =>
          instance
            .loginPopup({ scopes: [APPREG_SCOPE], prompt: "select_account" })
            .catch((e) => console.error(e))
        }
      >
        Aanmelden met Microsoft
      </Button>
    </div>
  );
}
